import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../lib/api';

const AuthCtx = createContext(null);

/**
 * AuthProvider — resilient to PWA background/foreground cycles.
 *
 * Rules:
 *  - A stored token means "logged in" until the SERVER explicitly says it isn't
 *    (HTTP 401 on /auth/me). Transient network errors (offline, DNS, 5xx) MUST
 *    NOT wipe the token or clear the user — PWAs lose network when the OS
 *    suspends them and we want the session to survive.
 *  - We cache the last-known user profile in localStorage so returning from the
 *    background shows the cached state instantly (zero-flicker) while a
 *    background revalidation happens silently.
 *  - We only re-validate when the tab becomes visible AFTER > 60s hidden, so
 *    quickly tabbing away and back is instant.
 */
const USER_CACHE_KEY = 'jixxeo_user_cache_v1';

function loadCachedUser() {
  try { return JSON.parse(localStorage.getItem(USER_CACHE_KEY) || 'null'); }
  catch { return null; }
}
function saveCachedUser(u) {
  try {
    if (u) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u));
    else localStorage.removeItem(USER_CACHE_KEY);
  } catch {}
}

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(() => (localStorage.getItem('jixxeo_token') ? loadCachedUser() : null));
  const [loading, setLoading] = useState(true);
  const [publicSettings, setPublicSettings] = useState(null);
  const lastHiddenAtRef = useRef(0);
  const inFlightRef = useRef(false);

  const setUser = useCallback((u) => {
    setUserState(u);
    saveCachedUser(u);
  }, []);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    const token = localStorage.getItem('jixxeo_token');
    if (!token) {
      setUserState(null); saveCachedUser(null);
      if (!silent) setLoading(false);
      return null;
    }
    if (inFlightRef.current) return user;
    inFlightRef.current = true;
    try {
      const { data } = await api.get('/auth/me');
      setUserState(data);
      saveCachedUser(data);
      return data;
    } catch (e) {
      // ONLY wipe the token when the server explicitly rejects it (401).
      // Network drops (no response), 5xx, timeouts etc. keep the cached user.
      if (e?.response?.status === 401) {
        localStorage.removeItem('jixxeo_token');
        saveCachedUser(null);
        setUserState(null);
      }
      return null;
    } finally {
      inFlightRef.current = false;
      if (!silent) setLoading(false);
    }
  }, [user]);

  // Initial load
  useEffect(() => {
    refresh();
    api.get('/settings/public').then(r => setPublicSettings(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the session alive across PWA background/foreground cycles.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenAtRef.current = Date.now();
        return;
      }
      // Returned to foreground. Only revalidate silently if we were hidden > 60s.
      const hiddenMs = Date.now() - (lastHiddenAtRef.current || Date.now());
      if (hiddenMs > 60_000 && localStorage.getItem('jixxeo_token')) {
        refresh({ silent: true });
      }
    };
    const onFocus = () => {
      // Treat focus like a soft visibility check for browsers that don't fire
      // visibilitychange reliably in standalone PWAs.
      if (localStorage.getItem('jixxeo_token') && !user) {
        refresh({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onFocus);
    };
  }, [refresh, user]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('jixxeo_token', data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    localStorage.setItem('jixxeo_token', data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('jixxeo_token');
    saveCachedUser(null);
    setUserState(null);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, register, logout, refresh, publicSettings }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
